import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import TitanLogo from "@/components/TitanLogo";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back, admin.");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0d2040] flex items-center justify-center px-4 py-8 sm:py-12"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6 sm:mb-8" data-testid="admin-login-logo-link">
          <TitanLogo variant="light" size="lg" />
        </Link>

        <form
          onSubmit={submit}
          className="bg-white px-5 py-6 sm:p-8 booking-widget"
          data-testid="admin-login-form"
          noValidate
        >
          <div className="flex items-center gap-2 mb-1">
            <Lock size={16} className="text-[#e63946]" />
            <span className="eyebrow">Admin access</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0d2040]">Sign in</h1>
          <p className="text-sm text-gray-600 mt-1.5">
            Enter your admin email and password to manage Titan.
          </p>

          <div className="mt-6">
            <label htmlFor="admin-email" className="titan-label">Email</label>
            <input
              id="admin-email"
              type="email"
              inputMode="email"
              className="titan-input titan-input--mobile"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoCapitalize="none"
              spellCheck="false"
              placeholder="you@titancarrental.com"
              data-testid="admin-login-email"
            />
          </div>

          <div className="mt-4">
            <label htmlFor="admin-password" className="titan-label">Password</label>
            <div className="relative">
              <input
                id="admin-password"
                type={showPw ? "text" : "password"}
                className="titan-input titan-input--mobile pr-12"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Your password"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-[#0d2040]"
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="admin-login-toggle-password"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary mt-6 w-full justify-center min-h-[48px] text-sm uppercase tracking-widest font-bold disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="admin-login-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
          </button>

          <Link
            to="/"
            className="text-xs text-gray-500 hover:text-[#0d2040] block text-center mt-5"
            data-testid="admin-login-back"
          >
            ← Back to website
          </Link>
        </form>

        <p className="text-[0.7rem] text-white/50 text-center mt-5 leading-relaxed px-2">
          Authorised personnel only. All sign-in attempts are logged and rate-limited.
        </p>
      </div>
    </div>
  );
}
